import React, { useState, useEffect } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { useSidebar } from "../../SidebarContext";
import user from "../../Img/user.webp";
import Swal from "sweetalert2";

export default function LeaderboardList() {
    const { isSidebarOpen } = useSidebar();
    const url = process.env.REACT_APP_LINK;
    const [rankingData, setRankingData] = useState([]);
    const [watchTimeData, setWatchTimeData] = useState([]);
    const [activeTab, setActiveTab] = useState("subscribers");
    const token = localStorage.getItem("token");
    const [winingAmount, setWiningAmount] = useState(0);
    const [eventId, setEventId] = useState(null);
    const [eventStatus, setEventStatus] = useState(false);
    const [loading, setLoading] = useState(false);
    const [eventTitle, setEventTitle] = useState("");
    const [watchWiningAmount, setwatchWiningAmount] = useState(0);

    // 🔹 Fetch Event Info
    const fetchDataEvent = async () => {
        try {
            const response = await fetch(`${url}/api/event`);
            const res = await response.json();

            setWiningAmount(parseFloat(res?.data[0].win_amount));
            setwatchWiningAmount(parseFloat(res?.data[0].watch_amount))
            setEventId(res?.data[0].id);
            setEventStatus(res?.data[0].eventStatus || false);
            setEventTitle(res?.data[0].title);
        } catch (error) {
            console.log(error);
        }
    };

    // 🔹 Fetch Leaderboard Data
    const fetchData = async () => {
        try {
            const response = await fetch(`${url}/api/analytics`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            });
            const res = await response.json();

            const grouped = {};
            res.data.forEach((item) => {
                const key = `${item.user_id}_${item.video_id}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        user_id: item.user_id,
                        user_name: item.user_name,
                        video_id: item.video_id,
                        subscribers_count: 0,
                        estimate_earning: 0,
                        views_count: 0,
                        watch_time: Number(item.watch_time || 0),
                        videoData: item.videoData || null, // ✅ preserve title, links, etc.

                    };
                }
                grouped[key].subscribers_count += Number(item.subscribers_count || 0);
                grouped[key].estimate_earning += Number(item.estimate_earning || 0);
                grouped[key].views_count += Number(item.views_count || 0);
                grouped[key].watch_time += Number(item.watch_time || 0);
            });

            // Sort by subscribers
            const sortedSubs = Object.values(grouped).sort(
                (a, b) => b.subscribers_count - a.subscribers_count
            );

            const rankedSubs = sortedSubs.map((item, index) => ({
                ...item,
                rank: index + 1,
            }));

            const totalSubscribers = rankedSubs.reduce(
                (sum, u) => sum + u.subscribers_count,
                0
            );

            const ans =
                totalSubscribers > 0 ? parseFloat(winingAmount) / totalSubscribers : 0;

            const withEarnings = rankedSubs.map((item) => ({
                ...item,
                earning: (item.subscribers_count * ans).toFixed(2),
            }));

            setRankingData(withEarnings);

            // 🔹 Watch time ranking
            // const sortedWatch = Object.values(grouped)
            //     .sort((a, b) => b.watch_time - a.watch_time)
            //     .map((item, index) => ({
            //         ...item,
            //         rank: index + 1,
            //     }));

            // const totalWatchTime = sortedWatch.reduce(
            //     (sum, u) => sum + u.watch_time,
            //     0
            // );

            // const ans1 =
            //     totalWatchTime > 0
            //         ? parseFloat(watchWiningAmount) / totalWatchTime
            //         : 0;

            // const withWatch = sortedWatch.map((item) => ({
            //     ...item,
            //     earningwatch: (item.watch_time * ans1).toFixed(2),
            // }));
            // setWatchTimeData(withWatch);

            // ✅ Prepare view count ranking
            const viewRank = Object.values(grouped)
                .sort((a, b) => b.views_count - a.views_count)
                .map((item, index) => ({
                    ...item,
                    rank: index + 1,
                }));

            const totalViews = viewRank.reduce(
                (sum, u) => sum + u.views_count,
                0
            );

            const perViewEarn =
                totalViews > 0
                    ? parseFloat(watchWiningAmount) / totalViews
                    : 0;

            const withViews = viewRank.map((item) => ({
                ...item,
                earningwatch: (item.views_count * perViewEarn).toFixed(2),
            }));

            setWatchTimeData(withViews);



        } catch (error) {
            console.log(error);
        }
    };

    // 🔹 Convert minutes → Hours & Minutes
    const formatWatchTime = (minutes) => {
        const hrs = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hrs}h ${mins}m`;
    };

    // 🔹 Complete Event
    const handleComplete = async () => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to mark this submission as complete?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, complete it!",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await fetch(
                    `${url}/api/event/status/complete/${eventId}`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ eventStatus: true }),
                    }
                );

                const res = await response.json();
                if (response.ok) {
                    Swal.fire("Completed!", res.message || "Event marked complete.", "success");
                    await Promise.all([saveEventEarnings(rankingData), saveEventWatch(watchTimeData)]);
                    setEventStatus(true);
                } else {
                    Swal.fire("Error!", res.error || "Something went wrong.", "error");
                }
            } catch (error) {
                console.error(error);
                Swal.fire("Error!", "Something went wrong.", "error");
            } finally {
                setLoading(false);
            }
        }
    };

    // 🔹 Pending Event
    const handlePending = async () => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to mark this submission as pending?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes",
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const response = await fetch(
                    `${url}/api/event/status/complete/${eventId}`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ eventStatus: false }),
                    }
                );

                const res = await response.json();
                if (response.ok) {
                    Swal.fire("Pending!", res.message || "Event marked pending.", "success");
                    await handlePendingMain(eventId);
                    setEventStatus(false);
                } else {
                    Swal.fire("Error!", res.error || "Something went wrong.", "error");
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handlePendingMain = async () => {
        try {
            const response = await fetch(
                `${url}/api/event/earnings/${eventId}/pending`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ payStatus: "Pending" }),
                }
            );
            const res = await response.json();
            if (response.ok) {
                await fetchDataEvent();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const saveEventEarnings = async (earningsData) => {
        try {
            const payload = {
                event_id: eventId,
                event_name: eventTitle,
                earnings: earningsData.map((player) => ({
                    ...player,
                    type: "Subscribers",
                    earning: player.earning,
                })),

            };

            const res = await fetch(`${url}/api/event/all/earnings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (res.ok) await fetchDataEvent();
        } catch (error) {
            console.error("Error saving event earnings:", error);
        }
    };

    const saveEventWatch = async (earningsData) => {
        try {
            const payload = {
                event_id: eventId,
                event_name: eventTitle,
                earnings: earningsData.map((player) => ({
                    ...player,
                    watchtime: player.watch_time,
                    type: "Watch",
                    earning: player.earningwatch,
                })),

            };

            const res = await fetch(`${url}/api/event/all/earnings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (res.ok) await fetchDataEvent();
        } catch (error) {
            console.error("Error saving event earnings:", error);
        }
    };
    useEffect(() => {
        fetchDataEvent();
    }, []);

    useEffect(() => {
        if (winingAmount) fetchData();
    }, [winingAmount, eventStatus]);

    const dataToShow = activeTab === "subscribers" ? rankingData : watchTimeData;

    return (
        <div className={`open-close ${isSidebarOpen ? "open" : "closed"}`}>
            <div className="oms-header">
                <h1 className="dashboard-title">Ranking</h1>

                {eventStatus ? (
                    <button className="oms-add-btn" onClick={handlePending}>
                        Mark as Pending
                    </button>
                ) : (
                    <button className="oms-add-btn" onClick={handleComplete}>
                        Mark as Completed
                    </button>
                )}
            </div>

            {/* 🔹 Tabs */}
            <div className="tab-buttons" style={{ marginBottom: "15px" }}>
                <button
                    className={`tab-btn ${activeTab === "subscribers" ? "active" : ""}`}
                    onClick={() => setActiveTab("subscribers")}
                >
                    Subscribers
                </button>
                {/* <button
                    className={`tab-btn ${activeTab === "watchtime" ? "active" : ""}`}
                    onClick={() => setActiveTab("watchtime")}
                >
                    Watch Time
                </button> */}

                <button
                    className={`tab-btn ${activeTab === "views" ? "active" : ""}`}
                    onClick={() => setActiveTab("views")}
                >
                    Views
                </button>

            </div>

            {/* Loader */}
            {loading ? (
                <div className="loader"></div>
            ) : (
                <div className="leaderboard">
                    <table className="leaderboard-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>PARTICIPANTS</th>
                                <th>Title</th>

                                <th>ID</th>
                                {activeTab === "subscribers" ? (
                                    <>
                                        <th>SUBSCRIBERS</th>
                                        <th>RANK</th>
                                        <th>EARNING</th>
                                    </>
                                ) : (
                                    <>
                                        <th>VIEWS</th>
                                        <th>RANK</th>
                                        <th>EARNING</th>
                                    </>
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            {dataToShow.length === 0 ? (
                                // 🔹 Empty State Row
                                <tr>
                                    <td colSpan={activeTab === "subscribers" ? 6 : 6}>
                                        <div className="text-center py-6 text-gray-500 font-medium">
                                            No data available
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                dataToShow.map((player, idx) => (
                                    <tr
                                        key={player.user_id + "_" + player.video_id}
                                        className={idx === 0 ? "highlight" : ""}
                                    >
                                        <td>{idx + 1}.</td>
                                        <td>
                                            <div className="network">
                                                <img src={user} alt={player.user_name} />
                                                <div className="info">
                                                    <span className="name">{player.user_name}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ textAlign: "center" }}>{player.videoData?.title}</td>

                                        <td style={{ textAlign: "center" }}>{player.user_id}</td>

                                        {activeTab === "subscribers" ? (
                                            <>
                                                <td style={{ textAlign: "center" }}>
                                                    {player.subscribers_count}
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    #{player.rank}{" "}
                                                    {idx === 0 ? (
                                                        <ArrowUp size={16} color="green" />
                                                    ) : (
                                                        <ArrowDown size={16} color="red" />
                                                    )}
                                                </td>
                                                <td style={{ textAlign: "center" }}>₹{player.earning}</td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={{ textAlign: "center" }}>
                                                    {player.views_count}                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    #{player.rank}{" "}
                                                    {idx === 0 ? (
                                                        <ArrowUp size={16} color="green" />
                                                    ) : (
                                                        <ArrowDown size={16} color="red" />
                                                    )}
                                                </td>
                                                <td style={{ textAlign: "center" }}>₹{player.earningwatch}</td>
                                            </>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>

    )
}